import React from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getQuizzes, deleteQuiz, updateQuizTitle, regenerateQuiz, restartQuiz } from "@/features/quizzes/api";
import { QuizListItem } from "./QuizListItem";
import { QuizListSkeleton } from "./QuizListSkeleton";
import { Card } from "@/components/ui/card";
import { toast } from "@/hooks/use-toast";
import type { QuizEditData } from "./QuizEditModal";
import type { Quiz } from "@/features/quizzes/types";

interface MyQuizzesListProps {
  userId: string;
  contentId?: string | null;
  onOpenQuiz?: (quiz: Quiz) => void;
}

export const MyQuizzesList: React.FC<MyQuizzesListProps> = ({
  userId,
  contentId,
  onOpenQuiz,
}) => {
  const queryClient = useQueryClient();

  const {
    data: quizzes,
    isLoading,
  } = useQuery({
    queryKey: ["quizzes", userId, contentId],
    queryFn: () => getQuizzes(userId, contentId),
    enabled: !!userId,
  });

  const deleteMutation = useMutation({
    mutationFn: deleteQuiz,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["quizzes", userId, contentId] });
      toast({ title: "Quiz deleted" });
    },
    onError: () => {
      toast({ title: "Failed to delete quiz", variant: "destructive" });
    },
  });

  const editMutation = useMutation({
    mutationFn: ({ quizId, title }: { quizId: string; title: string }) =>
      updateQuizTitle(quizId, title),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["quizzes", userId, contentId] });
      toast({ title: "Quiz renamed" });
    },
    onError: () => {
      toast({ title: "Failed to rename quiz", variant: "destructive" });
    },
  });

  const handleEdit = (quizId: string, title: string) => {
    editMutation.mutate({ quizId, title });
  };

  const handleDelete = (quizId: string) => {
    deleteMutation.mutate(quizId);
  };

  const restartMutation = useMutation({
    mutationFn: restartQuiz,
    onSuccess: (_, quizId) => {
      queryClient.invalidateQueries({ queryKey: ["quizzes", userId, contentId] });
      toast({ title: "Quiz restarted" });
      const quiz = quizzes?.find((q) => q.id === quizId);
      if (quiz && onOpenQuiz) {
        onOpenQuiz(quiz);
      }
    },
    onError: () => {
      toast({ title: "Failed to restart quiz", variant: "destructive" });
    },
  });

  const handleRestart = (quizId: string) => {
    restartMutation.mutate(quizId);
  };

  const handleClick = (quizId: string) => {
    const quiz = quizzes?.find((q) => q.id === quizId);
    if (quiz && onOpenQuiz) {
      onOpenQuiz(quiz);
    }
  };

  const regenerateMutation = useMutation({
    mutationFn: ({ quizId, data }: { quizId: string; data: QuizEditData }) =>
      regenerateQuiz(quizId, data),
    onMutate: () => {
      toast({ title: "Regenerating quiz...", description: "Please wait while we generate new questions." });
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ["quizzes", userId, contentId] });
      toast({ title: "Quiz regenerated!", description: `${result.question_count} questions created.` });
    },
    onError: (error: Error) => {
      toast({ title: "Failed to regenerate quiz", description: error.message, variant: "destructive" });
    },
  });

  const handleRegenerate = (quizId: string, data: QuizEditData) => {
    regenerateMutation.mutate({ quizId, data });
  };

  if (isLoading) return <QuizListSkeleton />;

  if (!quizzes || quizzes.length === 0) {
    return (
      <Card className="rounded-2xl border border-primary/10 bg-card p-4 text-sm text-muted-foreground">
        No quizzes yet. Ask Shattara AI to generate a quiz from your content.
      </Card>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      {quizzes.map((quiz) => (
        <QuizListItem
          key={quiz.id}
          quiz={quiz}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onRestart={handleRestart}
          onClick={handleClick}
          onRegenerate={handleRegenerate}
          isRegenerating={regenerateMutation.isPending && regenerateMutation.variables?.quizId === quiz.id}
        />
      ))}
    </div>
  );
};
